/**
 * EditTripDialog · v2.7.0 batch DC
 * =================================
 * Unified edit dialog for ANY trip (booked_trip OR completed ride). Reused
 * by Rides/Waybills + Dispatcher Book Ride + Driver Trips.
 *
 * Features:
 *   - Edit customer details (name, phone, email)
 *   - Edit pickup/dropoff/passengers
 *   - Edit payment method (cash/card/cashapp/venmo)
 *   - Edit final total — PIN-gated (supervisor 565968)
 *   - Supervisor price override (separate flow w/ reason)
 *   - Append an internal note (audit-trail-tied)
 *   - Generate / open / save / print the 2-page Receipt PDF
 *   - Full audit trail viewer
 */
import { useEffect, useState, useCallback } from "react";
import adminApi from "@/lib/adminApi";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  Loader2, Save, ShieldCheck, FileText, Printer, AlertTriangle,
  History, MessageSquare, DollarSign, User as UserIcon, MapPin,
  CreditCard, CheckCircle2, XCircle, Cloud,
} from "lucide-react";
import { toast } from "sonner";
import { DropboxGlyph } from "@/components/brands/DropboxBrand";

const PAYMENT_OPTIONS = [
  { value: "cash",    label: "Cash" },
  { value: "card",    label: "Card" },
  { value: "cashapp", label: "Cash App" },
  { value: "venmo",   label: "Venmo" },
];

export default function EditTripDialog({ tripId, open, onClose, onSaved }) {
  const [trip, setTrip] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [pin, setPin] = useState("");
  // Override flow
  const [overrideTotal, setOverrideTotal] = useState("");
  const [overrideReason, setOverrideReason] = useState("");
  const [overridePin, setOverridePin] = useState("");
  const [overrideRunning, setOverrideRunning] = useState(false);
  // Internal note
  const [noteText, setNoteText] = useState("");
  // v2.7.0 batch DE · Square hold capture / void
  const [captureAmount, setCaptureAmount] = useState("");
  const [capturePin, setCapturePin] = useState("");
  const [captureNote, setCaptureNote] = useState("");
  const [captureRunning, setCaptureRunning] = useState(false);
  const [voidPin, setVoidPin] = useState("");
  const [voidReason, setVoidReason] = useState("");
  const [voidRunning, setVoidRunning] = useState(false);
  // Local field edits
  const [edits, setEdits] = useState({});

  const load = useCallback(async () => {
    if (!tripId) return;
    setLoading(true);
    try {
      const { data } = await adminApi.get(`/admin/trips/${tripId}`);
      setTrip(data);
      setEdits({
        customer_name:   data.customer_name || "",
        customer_phone:  data.customer_phone || "",
        customer_email:  data.customer_email || "",
        origin:          data.origin || "",
        destination:     data.destination || "",
        passengers:      data.passengers || 1,
        payment_method:  data.payment_method || "cash",
        fare_total:      data.total ?? "",
        tip_amount:      data.tip_amount ?? "",
        notes:           data.notes || "",
        status:          data.status || "",
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message;
      toast.error(`Couldn't load trip: ${detail}`);
    } finally { setLoading(false); }
  }, [tripId]);

  useEffect(() => {
    if (open && tripId) load();
  }, [open, tripId, load]);

  const isFinancial = useCallback(() => {
    if (!trip) return false;
    const f = (a, b) => String(a ?? "") !== String(b ?? "");
    return (
      f(edits.fare_total, trip.total)
      || f(edits.tip_amount, trip.tip_amount)
      || f(edits.payment_method, trip.payment_method)
    );
  }, [edits, trip]);

  const save = async () => {
    if (!trip) return;
    const needsPin = isFinancial();
    if (needsPin && !pin) {
      return toast.error("Supervisor PIN required for fare / payment edits");
    }
    setSaving(true);
    try {
      const body = {
        customer_name:   edits.customer_name,
        customer_phone:  edits.customer_phone,
        customer_email:  edits.customer_email || null,
        origin:          edits.origin,
        destination:     edits.destination,
        passengers:      Number(edits.passengers) || 1,
        payment_method:  edits.payment_method,
        notes:           edits.notes,
      };
      // v2.7.65+E205 · Only send `status` when the operator actually
      // changed it. The dialog has no status control, so re-sending the
      // loaded value pushed a booked_trips label ("Completed",
      // "Pending Dispatch") into db.rides — whose readers filter on
      // lowercase states — and the edited trip vanished from both
      // Completed Rides and the Ride Coordinator queue.
      if (edits.status && edits.status !== trip.status) {
        body.status = edits.status;
      }
      if (edits.fare_total !== "" && edits.fare_total !== null) {
        body.fare_total = Number(edits.fare_total);
      }
      if (edits.tip_amount !== "" && edits.tip_amount !== null) {
        body.tip_amount = Number(edits.tip_amount);
      }
      if (needsPin) body.pin = pin;
      const { data } = await adminApi.put(`/admin/trips/${trip.id}`, body);
      setTrip(data);
      // Re-sync the form from what actually persisted so the field can
      // never show a value the database didn't accept.
      setEdits((prev) => ({
        ...prev,
        fare_total: data.total ?? "",
        tip_amount: data.tip_amount ?? "",
        payment_method: data.payment_method || prev.payment_method,
        status: data.status || "",
      }));
      setPin("");
      toast.success(`Trip updated · total $${Number(data.total ?? 0).toFixed(2)}`);
      onSaved?.(data);
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message;
      if (status === 403) toast.error("❌ Wrong supervisor PIN");
      else toast.error(`Save failed: ${detail}`);
    } finally { setSaving(false); }
  };

  const runOverride = async () => {
    if (!overrideTotal || Number(overrideTotal) <= 0) return toast.error("Enter a positive new total");
    if (!overridePin) return toast.error("Supervisor PIN required");
    setOverrideRunning(true);
    try {
      const { data } = await adminApi.post(`/admin/trips/${trip.id}/override`, {
        new_total: Number(overrideTotal),
        reason: overrideReason,
        pin: overridePin,
      });
      setTrip(data);
      setOverrideTotal(""); setOverrideReason(""); setOverridePin("");
      toast.success("Supervisor override applied — fare is final");
      onSaved?.(data);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 403) toast.error("❌ Wrong supervisor PIN");
      else toast.error(err?.response?.data?.detail || err?.message);
    } finally { setOverrideRunning(false); }
  };

  const addNote = async () => {
    if (!noteText.trim()) return;
    try {
      await adminApi.post(`/admin/trips/${trip.id}/notes`, { text: noteText.trim() });
      setNoteText("");
      load(); // refresh trip + audit
      toast.success("Note added");
    } catch (err) { toast.error(err?.response?.data?.detail || "Note failed"); }
  };

  // v2.7.0 batch DE · Capture a Square auth hold for the final fare
  const captureHold = async () => {
    if (!capturePin) return toast.error("Supervisor PIN required to capture a hold");
    setCaptureRunning(true);
    try {
      const body = { pin: capturePin };
      if (captureAmount !== "" && captureAmount !== null) {
        body.amount_cents = Math.round(Number(captureAmount) * 100);
      }
      if (captureNote.trim()) body.note = captureNote.trim();
      const { data } = await adminApi.post(`/admin/trips/${trip.id}/capture`, body);
      setCaptureAmount(""); setCapturePin(""); setCaptureNote("");
      const dollars = (data.captured_amount_cents / 100).toFixed(2);
      toast.success(`Captured $${dollars}${data.overage_cents > 0 ? ` · overage $${(data.overage_cents / 100).toFixed(2)} to collect manually` : ""}`);
      if (data.overage_note) toast.warning(data.overage_note, { duration: 12000 });
      load();
      onSaved?.();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message;
      if (status === 403) toast.error("❌ Wrong supervisor PIN");
      else toast.error(`Capture failed: ${detail}`);
    } finally { setCaptureRunning(false); }
  };

  const voidHold = async () => {
    if (!voidPin) return toast.error("Supervisor PIN required to void a hold");
    setVoidRunning(true);
    try {
      await adminApi.post(`/admin/trips/${trip.id}/void-hold`, {
        pin: voidPin, reason: voidReason || undefined,
      });
      setVoidPin(""); setVoidReason("");
      toast.success("Hold voided · funds released to the rider");
      load();
      onSaved?.();
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || err?.message;
      if (status === 403) toast.error("❌ Wrong supervisor PIN");
      else toast.error(`Void failed: ${detail}`);
    } finally { setVoidRunning(false); }
  };

  const openReceipt = () => {
    const tok = localStorage.getItem("rtg_admin_token") || "";
    // Build an authed URL via a one-shot fetch → blob → open (browser
    // can't otherwise attach Authorization header to a window.open)
    adminApi.get(`/admin/trips/${trip.id}/receipt`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const w = window.open(url, "_blank");
        if (!w) toast.error("Pop-up blocked — allow pop-ups to view receipt");
      })
      .catch((err) => toast.error(`Receipt generation failed: ${err?.message}`));
  };

  const downloadReceipt = () => {
    adminApi.get(`/admin/trips/${trip.id}/receipt`, { responseType: "blob" })
      .then((res) => {
        const blob = new Blob([res.data], { type: "application/pdf" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `Receipt-Waybill-${trip.id}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      })
      .catch((err) => toast.error(`Download failed: ${err?.message}`));
  };

  // v2.7.54+E30 · One-click "Save Trip Receipt to Dropbox" — calls the
  // backend endpoint that renders the same 2-page PDF and pushes it to
  // /Receipts/{YYYY-MM}/Receipt-Waybill-{trip_id}.pdf in the Bullertaxi
  // Dropbox-Business account. Requires write-tier access (backend
  // enforces; UI shows a clear error if blocked).
  const [savingToDropbox, setSavingToDropbox] = useState(false);
  const saveToDropbox = async () => {
    setSavingToDropbox(true);
    try {
      const { data } = await adminApi.post(
        "/admin/dropbox/files/save-trip-receipt",
        { trip_id: trip.id },
      );
      toast.success(`Saved to Dropbox`, {
        description: data.dropbox_path,
        duration: 6000,
      });
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Save failed";
      const status = err?.response?.status;
      if (status === 403) {
        toast.error("Saving receipts to Dropbox requires Developer, Operating-Partner, or Owner privileges.");
      } else if (status === 502 && /not connected/i.test(detail)) {
        toast.error("Dropbox not connected — set it up via Admin Console → Corporate File Sharing → Dropbox - Business.");
      } else {
        toast.error(`Dropbox save failed: ${detail}`);
      }
    } finally {
      setSavingToDropbox(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="edit-trip-dialog">
        <DialogHeader>
          <DialogTitle className="text-amber-300 flex items-center gap-2">
            <FileText className="h-5 w-5"/>Edit Trip · {tripId}
            {trip?.supervisor_override && (
              <Badge variant="outline" className="bg-orange-900/30 border-orange-700 text-orange-300 text-[10px]">
                <ShieldCheck className="h-3 w-3 mr-1"/>Supervisor Override Locked
              </Badge>
            )}
          </DialogTitle>
        </DialogHeader>

        {loading && (
          <div className="text-center py-12"><Loader2 className="h-5 w-5 animate-spin mx-auto text-zinc-500"/></div>
        )}

        {trip && !loading && (
          <div className="space-y-4">
            {/* Top action row */}
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={openReceipt} data-testid="trip-receipt-open" className="text-amber-300 border-amber-700/40 hover:bg-amber-900/20">
                <FileText className="h-3.5 w-3.5 mr-1"/>Open Receipt PDF
              </Button>
              <Button size="sm" variant="outline" onClick={downloadReceipt} data-testid="trip-receipt-download" className="text-amber-300 border-amber-700/40 hover:bg-amber-900/20">
                <Printer className="h-3.5 w-3.5 mr-1"/>Download / Print
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={saveToDropbox}
                disabled={savingToDropbox}
                data-testid="trip-receipt-save-dropbox"
                className="text-sky-300 border-sky-700/40 hover:bg-sky-900/20"
                title="Save the 2-page receipt PDF to the team's Dropbox under /Receipts/{YYYY-MM}/. Requires write-tier (Owner/Developer/Operating-Partner)."
              >
                {savingToDropbox ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin"/>
                ) : (
                  <DropboxGlyph size={14} className="mr-1"/>
                )}
                Save to Dropbox
              </Button>
            </div>

            {/* Customer / trip details */}
            <Card className="bg-zinc-900 border-zinc-800 p-4 space-y-3">
              <h3 className="text-sm font-bold text-zinc-200 flex items-center gap-2"><UserIcon className="h-4 w-4 text-amber-400"/>Customer</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <FieldRow label="Name"   value={edits.customer_name}   onChange={(v) => setEdits({ ...edits, customer_name: v })}   testid="trip-edit-name"/>
                <FieldRow label="Phone"  value={edits.customer_phone}  onChange={(v) => setEdits({ ...edits, customer_phone: v })}  testid="trip-edit-phone"/>
                <FieldRow label="Email"  value={edits.customer_email}  onChange={(v) => setEdits({ ...edits, customer_email: v })}  testid="trip-edit-email"/>
                <FieldRow label="Passengers" type="number" min={1} max={8} value={edits.passengers} onChange={(v) => setEdits({ ...edits, passengers: v })} testid="trip-edit-passengers"/>
              </div>
              <h3 className="text-sm font-bold text-zinc-200 flex items-center gap-2 pt-2"><MapPin className="h-4 w-4 text-amber-400"/>Route</h3>
              <FieldRow label="Pickup"   value={edits.origin}      onChange={(v) => setEdits({ ...edits, origin: v })}      testid="trip-edit-origin"/>
              <FieldRow label="Dropoff"  value={edits.destination} onChange={(v) => setEdits({ ...edits, destination: v })} testid="trip-edit-destination"/>

              <h3 className="text-sm font-bold text-zinc-200 flex items-center gap-2 pt-2"><DollarSign className="h-4 w-4 text-amber-400"/>Fare &amp; Payment</h3>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <FieldRow label="Final Total ($)" type="number" step="0.01" value={edits.fare_total} onChange={(v) => setEdits({ ...edits, fare_total: v })} testid="trip-edit-total"/>
                <FieldRow label="Tip ($)" type="number" step="0.01" value={edits.tip_amount} onChange={(v) => setEdits({ ...edits, tip_amount: v })} testid="trip-edit-tip"/>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Payment</label>
                  <select
                    value={edits.payment_method}
                    onChange={(e) => setEdits({ ...edits, payment_method: e.target.value })}
                    data-testid="trip-edit-payment"
                    className="w-full bg-zinc-950 border border-zinc-700 rounded px-3 py-2 text-sm text-zinc-100"
                  >
                    {PAYMENT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </div>
              </div>
              <FieldRow label="Public notes" value={edits.notes} onChange={(v) => setEdits({ ...edits, notes: v })} testid="trip-edit-notes" textarea/>

              {/* PIN row — only required when financial fields differ */}
              {isFinancial() && (
                <div className="bg-amber-900/20 border border-amber-700/40 rounded p-3 flex items-end gap-2">
                  <div className="flex-1">
                    <label className="text-[10px] uppercase tracking-wider text-amber-300 font-bold flex items-center gap-1">
                      <ShieldCheck className="h-3 w-3"/>Supervisor PIN required for fare / payment changes
                    </label>
                    <Input type="password" value={pin} onChange={(e) => setPin(e.target.value)} placeholder="••••••" data-testid="trip-edit-pin" className="bg-zinc-950 border-zinc-700 mt-1"/>
                  </div>
                </div>
              )}

              <div className="flex gap-2 pt-2">
                <Button onClick={save} disabled={saving} data-testid="trip-edit-save" className="bg-amber-400 hover:bg-amber-300 text-zinc-900 font-bold">
                  {saving ? <><Loader2 className="h-4 w-4 mr-1 animate-spin"/>Saving…</> : <><Save className="h-4 w-4 mr-1"/>Save</>}
                </Button>
                <Button variant="outline" onClick={onClose}>Cancel</Button>
              </div>
            </Card>

            {/* Supervisor override */}
            <Card className="bg-orange-950/20 border-orange-700/40 p-4 space-y-2">
              <h3 className="text-sm font-bold text-orange-300 flex items-center gap-2">
                <AlertTriangle className="h-4 w-4"/>Supervisor Price Override
                <Badge variant="outline" className="border-orange-700/40 text-orange-300 text-[10px]">FINAL FARE</Badge>
              </h3>
              <div className="text-xs text-zinc-400">Apply an absolute final total that bypasses automated recalculation. Logged + locked in the audit trail.</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                <FieldRow label="New total ($)" type="number" step="0.01" value={overrideTotal} onChange={setOverrideTotal} testid="trip-override-total"/>
                <FieldRow label="Reason"        value={overrideReason} onChange={setOverrideReason} testid="trip-override-reason"/>
                <div>
                  <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Supervisor PIN</label>
                  <Input type="password" value={overridePin} onChange={(e) => setOverridePin(e.target.value)} placeholder="••••••" data-testid="trip-override-pin" className="bg-zinc-950 border-zinc-700 mt-1"/>
                </div>
              </div>
              <Button
                onClick={runOverride}
                disabled={overrideRunning || !overrideTotal || !overridePin}
                data-testid="trip-override-apply"
                className="bg-orange-500 hover:bg-orange-400 text-zinc-900 font-bold"
              >{overrideRunning ? <><Loader2 className="h-4 w-4 mr-1 animate-spin"/>Applying…</> : "Apply Override"}</Button>
              {trip.supervisor_override && (
                <div className="mt-2 text-xs text-orange-200 bg-orange-900/30 border border-orange-700/30 rounded p-2 font-mono">
                  Last override: ${trip.supervisor_override.new_total?.toFixed(2)} by {trip.supervisor_override.actor} · reason: {trip.supervisor_override.reason || "—"}
                </div>
              )}
            </Card>

            {/* v2.7.0 batch DE · Square card-hold capture / void */}
            {trip.square_payment_id && (
              <Card className="bg-emerald-950/20 border-emerald-700/40 p-4 space-y-3" data-testid="trip-square-hold-panel">
                <h3 className="text-sm font-bold text-emerald-300 flex items-center gap-2">
                  <CreditCard className="h-4 w-4"/>Card Hold (Square)
                  <Badge variant="outline" className="border-emerald-700/40 text-emerald-300 text-[10px]" data-testid="trip-square-hold-status">
                    {(trip.square_payment_status || "UNKNOWN").toUpperCase()}
                  </Badge>
                  {trip.card_snapshot?.brand && (
                    <span className="ml-auto text-[10px] text-zinc-400 font-mono">
                      {trip.card_snapshot.brand} •••{trip.card_snapshot.last4 || "????"}
                    </span>
                  )}
                </h3>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Hold Amount</div>
                    <div className="text-zinc-200 font-bold" data-testid="trip-square-hold-amount">
                      ${((trip.square_hold_amount_cents || 0) / 100).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Captured</div>
                    <div className="text-zinc-200 font-bold" data-testid="trip-square-captured-amount">
                      ${((trip.square_captured_amount_cents || 0) / 100).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Overage</div>
                    <div className={(trip.square_overage_cents || 0) > 0 ? "text-orange-300 font-bold" : "text-zinc-200 font-bold"}>
                      ${((trip.square_overage_cents || 0) / 100).toFixed(2)}
                    </div>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-wider text-zinc-500">Payment ID</div>
                    <div className="text-zinc-400 text-[10px] truncate font-mono" title={trip.square_payment_id}>
                      {trip.square_payment_id?.slice(0, 14)}…
                    </div>
                  </div>
                </div>

                {/* CAPTURE PANEL — only when status is AUTHORIZED/APPROVED */}
                {["AUTHORIZED", "APPROVED"].includes((trip.square_payment_status || "").toUpperCase()) && (
                  <div className="border-t border-emerald-700/30 pt-3 space-y-2">
                    <div className="text-xs text-zinc-300">
                      Capture the final fare from the hold. Leave amount blank to capture the full hold (${((trip.square_hold_amount_cents || 0) / 100).toFixed(2)}).
                      If the meter ran higher than the hold, capture the full hold and collect the overage manually.
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                      <FieldRow
                        label={`Capture Amount ($) · hold = $${((trip.square_hold_amount_cents || 0) / 100).toFixed(2)}`}
                        type="number" step="0.01"
                        value={captureAmount} onChange={setCaptureAmount}
                        testid="trip-square-capture-amount"
                      />
                      <FieldRow
                        label="Internal Note (optional)"
                        value={captureNote} onChange={setCaptureNote}
                        testid="trip-square-capture-note"
                      />
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Supervisor PIN</label>
                        <Input
                          type="password" value={capturePin}
                          onChange={(e) => setCapturePin(e.target.value)}
                          placeholder="••••••"
                          data-testid="trip-square-capture-pin"
                          className="bg-zinc-950 border-zinc-700 mt-1"
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 pt-1">
                      <Button
                        onClick={captureHold}
                        disabled={captureRunning || !capturePin}
                        data-testid="trip-square-capture-apply"
                        className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-bold"
                      >
                        {captureRunning
                          ? <><Loader2 className="h-4 w-4 mr-1 animate-spin"/>Capturing…</>
                          : <><CheckCircle2 className="h-4 w-4 mr-1"/>Capture Hold</>}
                      </Button>
                      <div className="flex-1"/>
                      <Button
                        onClick={voidHold}
                        disabled={voidRunning || !voidPin}
                        data-testid="trip-square-void-apply"
                        variant="outline"
                        className="border-red-700/40 text-red-300 hover:bg-red-900/30"
                      >
                        {voidRunning
                          ? <><Loader2 className="h-4 w-4 mr-1 animate-spin"/>Voiding…</>
                          : <><XCircle className="h-4 w-4 mr-1"/>Void Hold (no charge)</>}
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 pt-2 border-t border-zinc-800">
                      <FieldRow
                        label="Void Reason (optional)"
                        value={voidReason} onChange={setVoidReason}
                        testid="trip-square-void-reason"
                      />
                      <div>
                        <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">Supervisor PIN (for void)</label>
                        <Input
                          type="password" value={voidPin}
                          onChange={(e) => setVoidPin(e.target.value)}
                          placeholder="••••••"
                          data-testid="trip-square-void-pin"
                          className="bg-zinc-950 border-zinc-700 mt-1"
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Status messaging for non-actionable states */}
                {["COMPLETED", "CAPTURED"].includes((trip.square_payment_status || "").toUpperCase()) && (
                  <div className="border-t border-emerald-700/30 pt-3 text-xs text-emerald-300 flex items-center gap-1.5">
                    <CheckCircle2 className="h-4 w-4"/>
                    Hold captured · ${((trip.square_captured_amount_cents || 0) / 100).toFixed(2)} charged on {trip.square_captured_at ? new Date(trip.square_captured_at).toLocaleString() : "—"}.
                  </div>
                )}
                {["CANCELED", "CANCELLED", "VOIDED"].includes((trip.square_payment_status || "").toUpperCase()) && (
                  <div className="border-t border-red-700/30 pt-3 text-xs text-red-300 flex items-center gap-1.5">
                    <XCircle className="h-4 w-4"/>
                    Hold voided · funds released to the rider on {trip.square_voided_at ? new Date(trip.square_voided_at).toLocaleString() : "—"}.
                  </div>
                )}
              </Card>
            )}

            {/* Internal notes */}
            <Card className="bg-zinc-900 border-zinc-800 p-4 space-y-2">
              <h3 className="text-sm font-bold text-zinc-200 flex items-center gap-2"><MessageSquare className="h-4 w-4 text-amber-400"/>Internal Notes</h3>
              <div className="flex gap-2">
                <Textarea value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add an internal note…" rows={2} data-testid="trip-internal-note-input" className="bg-zinc-950 border-zinc-700"/>
                <Button onClick={addNote} disabled={!noteText.trim()} data-testid="trip-internal-note-add" className="bg-amber-400 hover:bg-amber-300 text-zinc-900 self-end">Add</Button>
              </div>
              <div className="space-y-1 max-h-48 overflow-y-auto" data-testid="trip-internal-note-list">
                {(trip.internal_notes || []).slice().reverse().map((n, i) => (
                  <div key={n.id || i} className="p-2 rounded bg-zinc-950 border border-zinc-800 text-xs">
                    <div className="font-bold text-amber-300">{n.actor}</div>
                    <div className="text-zinc-400 text-[10px]">{new Date(n.ts).toLocaleString()}</div>
                    <div className="text-zinc-200 mt-1 whitespace-pre-wrap">{n.text}</div>
                  </div>
                ))}
                {(!trip.internal_notes || trip.internal_notes.length === 0) && (
                  <div className="text-xs text-zinc-500 italic">No internal notes yet.</div>
                )}
              </div>
            </Card>

            {/* Audit trail */}
            <Card className="bg-zinc-900 border-zinc-800 p-4 space-y-2">
              <h3 className="text-sm font-bold text-zinc-200 flex items-center gap-2"><History className="h-4 w-4 text-amber-400"/>Audit Trail</h3>
              <div className="space-y-1 max-h-72 overflow-y-auto" data-testid="trip-audit-list">
                {(trip.audit || []).map((a) => (
                  <div key={a.id} className="p-2 rounded bg-zinc-950 border border-zinc-800 text-xs flex flex-col">
                    <div className="flex items-center gap-2">
                      <span className={`px-1.5 py-0.5 rounded text-[10px] uppercase font-bold ${
                        a.action === "price_override" ? "bg-orange-900/40 text-orange-300 border border-orange-700/40" :
                        a.action === "edit"           ? "bg-amber-900/30 text-amber-200 border border-amber-700/30" :
                        a.action === "note_added"     ? "bg-zinc-800 text-zinc-300 border border-zinc-700" :
                        a.action === "finalize_booking" ? "bg-emerald-900/40 text-emerald-300 border border-emerald-700/40" :
                        "bg-zinc-800 text-zinc-400"
                      }`}>{a.action}</span>
                      <span className="text-zinc-400">{new Date(a.ts).toLocaleString()}</span>
                      <span className="text-zinc-500 ml-auto">{a.actor}{a.supervisor_override && " · SUPERVISOR"}</span>
                    </div>
                    {Object.keys(a.changes || {}).length > 0 && (
                      <pre className="text-[10px] text-zinc-400 mt-1 whitespace-pre-wrap font-mono">{JSON.stringify(a.changes, null, 2)}</pre>
                    )}
                    {a.note && <div className="text-zinc-400 text-[10px] mt-1 italic">&quot;{a.note}&quot;</div>}
                  </div>
                ))}
                {(!trip.audit || trip.audit.length === 0) && (
                  <div className="text-xs text-zinc-500 italic">No audit entries yet.</div>
                )}
              </div>
            </Card>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Close</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function FieldRow({ label, value, onChange, testid, type = "text", textarea = false, ...rest }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-zinc-500 font-bold">{label}</label>
      {textarea ? (
        <Textarea value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="bg-zinc-950 border-zinc-700 mt-1"/>
      ) : (
        <Input type={type} value={value ?? ""} onChange={(e) => onChange(e.target.value)} data-testid={testid} className="bg-zinc-950 border-zinc-700 mt-1" {...rest}/>
      )}
    </div>
  );
}
